/**
 * Shared in-memory cache for affiliate search results.
 *
 * Both useSponsoredSlots and useEditorPicks use Google Search API calls
 * that count against the user's daily search budget. This cache prevents
 * redundant calls when the same profile is active — results are reused
 * for CACHE_TTL_MS (1 hour) before a fresh fetch is required.
 *
 * The cache is module-scoped (singleton) so all hook instances share it.
 */

const CACHE_TTL_MS = 1 * 60 * 60 * 1000; // 1 hour — shorter so product variety improves between sessions
const MAX_CACHE_SIZE = 50; // cap to prevent unbounded growth in long sessions

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

const cache = new Map<string, CacheEntry<unknown>>();

/** Remove all expired entries from the cache. */
function evictExpired(): void {
  const now = Date.now();
  for (const [key, entry] of cache) {
    if (now - entry.timestamp > CACHE_TTL_MS) {
      cache.delete(key);
    }
  }
}

/**
 * Return cached data if the key exists and hasn't expired, else null.
 */
export function getCached<T>(key: string): T | null {
  const entry = cache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.timestamp > CACHE_TTL_MS) {
    cache.delete(key);
    return null;
  }
  return entry.data as T;
}

/**
 * Store data in the cache with the current timestamp.
 * Evicts expired entries and enforces a max-size cap.
 */
export function setCached<T>(key: string, data: T): void {
  // Periodic cleanup: evict expired entries on every write
  evictExpired();
  // If still over cap after eviction, drop the oldest entry
  if (cache.size >= MAX_CACHE_SIZE) {
    const oldest = cache.keys().next().value;
    if (oldest !== undefined) cache.delete(oldest);
  }
  cache.set(key, { data, timestamp: Date.now() });
}

/**
 * Build a deterministic cache key from profile attributes.
 * Concerns are sorted so order doesn't matter.
 * Includes slotCount and optional AI analysis fingerprint so cache
 * invalidates when layout or AI keywords change.
 */
export function buildProfileCacheKey(
  prefix: string,
  skinType: string,
  concerns: string[],
  region: string,
  language?: string,
  slotCount?: number,
  aiFingerprint?: string,
): string {
  const parts = [prefix, skinType || 'unknown', [...concerns].sort().join(','), region];
  if (language) parts.push(language);
  if (slotCount !== undefined) parts.push(String(slotCount));
  if (aiFingerprint) parts.push(aiFingerprint);
  return parts.join('|');
}
